<form class="card mt-2">
  <div class="card-body">
    <h2 class="mb-3">Reports</h2>

    <div class="btn-group mb-3" role="group" *ngIf="canViewEmployeePayrollReports">
      <button type="button" routerLink="/employee/census" class="btn btn-outline-secondary">Census Report</button>
      <button type="button" routerLink="/employee/terminated" class="btn btn-outline-secondary">Separation Report</button>
    </div>

    <div class="btn-group mb-3 ml-2" role="group">
      <button *ngFor="let report of reports"
              type="button"
              class="btn"
              [ngClass]="reportType === report.value ? 'btn-primary' : 'btn-outline-primary'"
              (click)="selectReport(report.value)">
        {{report.label}}
      </button>
    </div>

    <div *ngIf="errorMessage" class="alert alert-danger">{{errorMessage}}</div>
    <div *ngIf="successMessage" class="alert alert-success">{{successMessage}}</div>

    <div class="row align-items-end mb-3">
      <div class="col-12 col-lg-7">
        <label class="font-weight-bold">Upload CSV or Excel File</label>
        <input type="file" class="form-control" accept=".csv,.xlsx" (change)="onFileSelected($event)" />
      </div>
      <div class="col-12 col-lg-3 mt-2 mt-lg-0">
        <button type="button" class="btn btn-success w-100" [disabled]="uploading" (click)="uploadReport()">
          <i *ngIf="uploading" class="material-icons rotate text-muted">loop</i>
          <i *ngIf="!uploading" class="fas fa-upload"></i>
          Upload
        </button>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12 col-md-3">
        <label class="font-weight-bold">Start Date</label>
        <input type="date" class="form-control" [(ngModel)]="startDate" [ngModelOptions]="{standalone: true}" />
      </div>
      <div class="col-12 col-md-3">
        <label class="font-weight-bold">End Date</label>
        <input type="date" class="form-control" [(ngModel)]="endDate" [ngModelOptions]="{standalone: true}" />
      </div>
      <div class="col-12 col-md-4">
        <label class="font-weight-bold">Employee Search</label>
        <input type="text" class="form-control" placeholder="Name from match results" [(ngModel)]="userSearch" [ngModelOptions]="{standalone: true}" />
      </div>
      <div class="col-12 col-md-2 d-flex align-items-end mt-2 mt-md-0">
        <button type="button" class="btn btn-secondary mr-2" (click)="loadUploads()">
          <i class="fas fa-search"></i>
        </button>
        <button type="button" class="btn btn-outline-secondary" (click)="clearFilters()">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <i [hidden]="!loadingUploads" class="material-icons rotate text-muted">loop</i>
    <p-table [value]="uploads" [responsive]="true" [paginator]="true" [rows]="20">
      <ng-template pTemplate="header">
        <tr>
          <th width="90px"></th>
          <th>Uploaded</th>
          <th>File</th>
          <th>Uploaded By</th>
          <th>Status</th>
          <th>Rows</th>
          <th>Similarities</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-upload>
        <tr [class.table-active]="selectedUpload?.id === upload.id">
          <td>
            <button type="button" class="btn btn-secondary" [disabled]="!isUploadComplete(upload)" (click)="viewMatches(upload)" title="View Results">
              <i class="fas fa-search"></i>
            </button>
          </td>
          <td>{{upload.uploadedDate | date: 'MM/dd/yyyy h:mm a'}}</td>
          <td>{{upload.fileName}}</td>
          <td>{{upload.uploadedByName}}</td>
          <td>
            <span>{{upload.processingStatus || 'Completed'}}</span>
            <span *ngIf="isUploadProcessing(upload)" class="ml-2">
              <i class="material-icons rotate text-muted">loop</i>
            </span>
            <div *ngIf="isUploadFailed(upload) && upload.processingError" class="text-danger small">{{upload.processingError}}</div>
          </td>
          <td>{{isUploadComplete(upload) ? upload.sourceRowCount : '-'}}</td>
          <td>
            <span *ngIf="isUploadComplete(upload)">{{upload.matchCount}}/{{upload.employeeCountChecked}} employees</span>
            <span *ngIf="!isUploadComplete(upload)">-</span>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7">No uploads found.</td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</form>

<div class="card mt-3" *ngIf="selectedUpload && !isUploadComplete(selectedUpload)">
  <div class="card-body">
    <h3 class="mb-3">Results</h3>
    <div *ngIf="isUploadProcessing(selectedUpload)" class="alert alert-info mb-0">
      Report is {{selectedUpload.processingStatus | lowercase}}.
    </div>
    <div *ngIf="isUploadFailed(selectedUpload)" class="alert alert-danger mb-0">
      {{selectedUpload.processingError || 'Report processing failed.'}}
    </div>
  </div>
</div>

<div class="card mt-3" *ngIf="selectedUpload && isUploadComplete(selectedUpload)">
  <div class="card-body">
    <h3 class="mb-3">Results</h3>
    <i [hidden]="!loadingMatches" class="material-icons rotate text-muted">loop</i>
    <p-table [value]="matches" [responsive]="true" [paginator]="true" [rows]="20">
      <ng-template pTemplate="header">
        <tr>
          <th>Score</th>
          <th>Reason</th>
          <th>Uploaded Row</th>
          <th>Employee</th>
          <th>Uploaded DOB</th>
          <th>Employee DOB</th>
          <th>Uploaded NPI</th>
          <th>Employee NPI</th>
          <th>Status</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-match>
        <tr>
          <td>{{match.matchScore}}</td>
          <td>{{match.matchReason}}</td>
          <td>
            {{match.sourceLastName}}, {{match.sourceFirstName}}
            <span *ngIf="match.sourceBusinessName">({{match.sourceBusinessName}})</span>
          </td>
          <td>{{match.employeeLastName}}, {{match.employeeFirstName}}</td>
          <td>{{match.sourceBirthdate | date: 'MM/dd/yyyy'}}</td>
          <td>{{match.employeeBirthdate | date: 'MM/dd/yyyy'}}</td>
          <td>{{match.sourceNPI}}</td>
          <td>{{match.employeeNPI}}</td>
          <td>
            <span *ngIf="match.employeeTerminationDate">Separated</span>
            <span *ngIf="!match.employeeTerminationDate && match.employeeInactive">Inactive</span>
            <span *ngIf="!match.employeeTerminationDate && !match.employeeInactive">Active</span>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="9">No similarities found for this upload.</td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</div>
